import React, { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { addDays } from "date-fns";
import { setNewMyTasks } from "../../../actions/task";
import { button, icon, label, notifyIcon, number, quote } from "../../../config";
import CommonRecurrence from "../../../shared/components/CommonRecurrence/CommonRecurrence";
import { getNotification } from "../../../utils/common";
import { setDueDate } from "../tasks.helper";
import { addEditTaskRecurrence, handleTaskKeyUpdate } from "../tasks.service";
import RecurrenceCalendar from "./RecurrenceCalendar";
import { RecurringTaskContext } from "./RecurringTaskContext";
import "./recurringTask.scss";
import {
  createRecurrencePayload,
  getDatesBetweenDates,
  getExistingRecurrencePayload,
  getFrequencyDayDate,
  recurrenceAccess,
} from "./recurringTaskHelper";

/**
 * Contains recurring task related details
 * @author Muskan Thakur
 */
const SaveOrEditRecurrence = (props) => {
  const { show, togglePopup, anchor, recurrPopupRef, blurTimeoutRef, onOpen, onFocus, onBlur } = props;
  const {
    calenderValue,
    recurrencePattern,
    recur,
    duration,
    endBydateValue,
    endByCountValue,
    skipCountValue,
    recurrenceMode,
    edit,
    isSaveDisabled,
    setIsSaveDisabled,
    updatedPayload,
    setUpdatedPayload,
    skipDays,
    assignedId,
    taskAssignmentType,
    reminderValue,
    frequencyInterval,
    frequencyIntervalType,
    weeklyCustomRecurDay,
    monthlyCustomRecurDates,
  } = useContext(RecurringTaskContext);
  const { task, isNewTask } = useSelector((state) => state.taskSidebar);
  const { recurrenceDetails } = useSelector((state) => state.recurrence);
  const { allTasks } = useSelector((state) => state.tasks);
  const { id } = useSelector((state) => state.auth.user);
  const [popupAlign, setPopupAlign] = React.useState({
    horizontal: "right",
    vertical: "center",
  });
  const dispatch = useDispatch();
  const skipWeekends = skipDays.filter(function (e) {
    return e !== true;
  });

  const remainingRecurrence =
    duration?.value === number.TWO || duration?.value === number.THREE
      ? duration?.value === number.THREE
        ? endByCountValue
        : recurrencePattern === number.TWO
        ? null
        : getDatesBetweenDates(recur?.value, calenderValue, endBydateValue, skipWeekends, calenderValue, frequencyIntervalType?.value)
      : null;

  const nextRecurrenceCompletionBased = addDays(calenderValue, skipCountValue);

  /**
   * sets the updated payload for recurrence and popup alignment when in edit mode
   * @author Muskan Thakur
   */
  useEffect(() => {
    if (edit) {
      setUpdatedPayload(getExistingRecurrencePayload(recurrenceDetails));
      setPopupAlign({
        horizontal: "center",
        vertical: "center",
      });
    }
  }, [edit]);

  const hideOnBlur = () => {
    togglePopup(false);
  };

  /**
   * handles the process of saving or updating task recurrence details
   * @author Muskan Thakur
   */
  const handleSaveRecurrence = async () => {
    setIsSaveDisabled(true);

    const payload = createRecurrencePayload(
      recur,
      recurrencePattern,
      calenderValue,
      recurrenceMode,
      skipDays,
      duration,
      endBydateValue,
      endByCountValue,
      skipWeekends,
      reminderValue,
      remainingRecurrence,
      taskAssignmentType,
      task,
      id,
      skipCountValue,
      assignedId,
      frequencyInterval,
      frequencyIntervalType,
      weeklyCustomRecurDay,
      monthlyCustomRecurDates
    );

    const updatedValues = edit ? Object.fromEntries(Object.entries(payload).filter(([key, value]) => updatedPayload[key] !== value)) : null;

    if (edit) {
      updatedValues["taskId"] = task?.taskId;
      updatedValues["createdBy"] = id;
    }

    togglePopup(false);
    await addEditTaskRecurrence(edit ? updatedValues : payload);
    const frequencyDate = getFrequencyDayDate(frequencyIntervalType, calenderValue, weeklyCustomRecurDay, monthlyCustomRecurDates, recur?.value);
    const response = handleTaskKeyUpdate(isNewTask, "dueDate", frequencyDate);
    if (response) {
      const payload = setDueDate(allTasks, frequencyDate, task?.taskId);
      dispatch(setNewMyTasks(payload));
    }
  };

  /**
   * submit handler for the recurrence details
   */
  const handleSubmit = async () => {
    if (remainingRecurrence === number.ZERO && recurrencePattern === number.ONE) return getNotification(quote.SELECT_DATES, notifyIcon.WARNING_ICON);
    if (remainingRecurrence === number.ZERO && recurrencePattern === number.TWO)
      return getNotification(quote.SELECT_VALID_COUNT, notifyIcon.WARNING_ICON);
    if (recurrencePattern === number.TWO && duration?.value === number.TWO && nextRecurrenceCompletionBased > endBydateValue)
      return getNotification(quote.SELECT_VALID_END_DATE, notifyIcon.WARNING_ICON);
    if (recur?.value === number.FIVE && frequencyIntervalType?.value === number.TWO && !weeklyCustomRecurDay?.length)
      return getNotification(quote.SELECT_VALID_WEEK_DAY, notifyIcon.WARNING_ICON);
    if (recur?.value === number.FIVE && frequencyIntervalType?.value === number.THREE && !monthlyCustomRecurDates?.length)
      return getNotification(quote.SELECT_VALID_MONTH_DATE, notifyIcon.WARNING_ICON);
    if (duration?.value && recur?.value && calenderValue && !isSaveDisabled && recurrenceAccess(task)) return handleSaveRecurrence();
  };

  return (
    <Popup
      show={show}
      anchor={anchor.current}
      animate={false}
      className='dt-popup recurring-popup'
      popupAlign={popupAlign}
      onOpen={() => onOpen(recurrPopupRef)}>
      <div
        className='recurring-popup-inner'
        ref={recurrPopupRef}
        tabIndex={number.ZERO}
        onFocus={() => onFocus(blurTimeoutRef)}
        onBlur={() => onBlur(blurTimeoutRef, hideOnBlur)}>
        <div className='dt-popup-header d-flex justify-content-between align-items-center'>
          <div className='dt-popup-title'>{label.TASK_RECURRENCE}</div>
          <span className='dt-popup-cancel-btn' id='recurring-task-content-close-popup' onClick={() => togglePopup(false)}>
            <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
              {icon.CLOSE}
            </Tooltip>
          </span>
        </div>
        <div className='dt-popup-body'>
          <div className='form-row'>
            <div className='col-md-7'>
              <CommonRecurrence />
            </div>
            <div className='mx-3 border-left'></div>
            <div className='set-recurrence'>
              <RecurrenceCalendar />
            </div>
          </div>
        </div>
        <div className='dt-popup-footer text-right'>
          <button className='btn btn-ghost mr-2' id='recurring-task-content-cancel-recurrence' onClick={() => togglePopup(false)}>
            {button.CANCEL}
          </button>
          <button className='btn btn-primary' id='recurring-task-content-save-recurrence' onClick={() => handleSubmit()} disabled={isSaveDisabled}>
            {button.SAVE}
          </button>
        </div>
      </div>
    </Popup>
  );
};

export default SaveOrEditRecurrence;
